import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { keys } from "./keys";
import { keys as documentKeys } from "../../documents/api/keys";
import { deleteDriveFilesQuietly } from "../../documents/api/driveFiles";
import { useSpace } from "../../../hooks/useSpace";
import { enqueueSnackbar } from "../../../components/GlobalSnackbar/snackbarStore";

async function deleteProviderContract(contractId: string, documentId: string | null, spaceId: string) {
  // Read the file url before deleting anything, so we can clean up Drive after.
  let fileUrl: string | null = null;
  if (documentId) {
    const { data } = await apiClient
      .from('documents')
      .select('file_url')
      .eq('id', documentId)
      .single();
    fileUrl = data?.file_url ?? null;
  }

  // Delete the contract first: document_id is ON DELETE RESTRICT, so the
  // document can only be removed once nothing references it.
  const { error } = await apiClient
    .from('provider_contracts')
    .delete()
    .eq('id', contractId);

  if (error) {
    throw error;
  }

  if (documentId) {
    await deleteDriveFilesQuietly([fileUrl], spaceId);

    const { error: documentError } = await apiClient
      .from('documents')
      .delete()
      .eq('id', documentId);

    if (documentError) {
      throw documentError;
    }
  }
}

type Variables = {
  providerId: string;
  contractId: string;
  documentId: string | null;
}

export default function useDeleteProviderContract() {
  const queryClient = useQueryClient();
  const { currentSpace } = useSpace();

  return useMutation({
    mutationFn: ({ contractId, documentId }: Variables) => {
      if (!currentSpace) {
        throw new Error('Aucun espace sélectionné pour supprimer le contrat.');
      }

      return deleteProviderContract(contractId, documentId, currentSpace.id);
    },
    onSuccess: (_, { providerId }) => {
      queryClient.invalidateQueries({ queryKey: keys.getProviderContracts(providerId) });
      // The contract file also lived in documents (unified list, provider list/count).
      queryClient.invalidateQueries({ queryKey: documentKeys.documents });
      enqueueSnackbar('Contrat supprimé.', 'success');
    },
  });
}
